import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabaseClient'
import { Link, useNavigate } from 'react-router-dom'
import { Home, LogOut, TrendingUp, Target, Award, Clock } from 'lucide-react'
import { toast } from 'sonner'
import TypirLogo from '../components/TypirLogo'

export default function Dashboard() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadProfile()
  }, [user])

  const loadProfile = async () => {
    if (!user) return

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)

      // Handle no rows found
      if (!error && (!data || data.length === 0)) {
        // Profile doesn't exist, create one
        // Try to get full name from Google OAuth metadata
        const fullName = user.user_metadata?.full_name || 
                        user.user_metadata?.name || 
                        user.email?.split('@')[0] || 
                        'User'
        
        const { data: newProfileData, error: insertError } = await supabase
          .from('profiles')
          .insert([
            {
              id: user.id,
              email: user.email,
              username: fullName,
              last_trained_alphabet: 'a',
              current_difficulty: 'easy',
              words_per_minute: 0,
              last_accuracy: 100,
            },
          ])
          .select()

        if (insertError) throw insertError
        // Get first item from array response
        const newProfile = Array.isArray(newProfileData) ? newProfileData[0] : newProfileData
        setProfile(newProfile)
      } else if (error) {
        throw error
      } else {
        // Get first item from array response
        const profile = Array.isArray(data) ? data[0] : data
        setProfile(profile)
      }
    } catch (err) {
      console.error('Error loading profile:', err)
      console.error('Error details:', JSON.stringify(err, null, 2))
      toast.error(`Failed to load profile: ${err.message || 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  const handleSignOut = async () => {
    const { error } = await signOut()
    if (error) {
      toast.error('Failed to sign out')
    } else {
      navigate('/')
      toast.success('Signed out successfully')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="dashboard-page" style={{ background: '#000000' }}>
      {/* Header */}
      <nav className="px-8 py-6 border-b border-gray-800">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-6">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">
              <Home className="w-6 h-6" />
            </Link>
            <TypirLogo size={35} />
            <span className="text-gray-600">|</span>
            <h1 className="text-xl font-semibold text-gray-300">Dashboard</h1>
          </div>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
            data-testid="signout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>Sign Out</span>
          </button>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-8 py-12">
        {/* Profile Card */}
        <div className="stat-card rounded-2xl p-8 mb-8 border">
          <div className="flex items-center gap-6 mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white text-3xl font-bold">
              {profile?.username?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white mb-1" data-testid="user-name">
                {profile?.username || 'User'}
              </h2>
              <p className="text-gray-400">{profile?.email}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-4 bg-black rounded-xl border border-gray-800">
              <div className="w-12 h-12 bg-green-900 rounded-lg flex items-center justify-center">
                <Target className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Current Alphabet</p>
                <p className="text-2xl font-bold text-white">
                  {profile?.last_trained_alphabet?.toUpperCase() || 'A'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-black rounded-xl border border-gray-800">
              <div className="w-12 h-12 bg-green-900 rounded-lg flex items-center justify-center">
                <Award className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Current Difficulty</p>
                <p className="text-2xl font-bold text-white capitalize">
                  {profile?.current_difficulty || 'Easy'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="stat-card rounded-2xl p-6 border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-300">Words Per Minute</h3>
              <TrendingUp className="w-6 h-6 text-green-500" />
            </div>
            <p className="text-4xl font-bold text-green-500" data-testid="wpm-stat">
              {profile?.words_per_minute || 0}
            </p>
            <p className="text-sm text-gray-400 mt-2">WPM</p>
          </div>

          <div className="stat-card rounded-2xl p-6 border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-300">Last Accuracy</h3>
              <Target className="w-6 h-6 text-green-500" />
            </div>
            <p className="text-4xl font-bold text-green-500" data-testid="accuracy-stat">
              {profile?.last_accuracy || 100}
            </p>
            <p className="text-sm text-gray-400 mt-2">%</p>
          </div>

          <div className="stat-card rounded-2xl p-6 border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-300">Progress</h3>
              <Clock className="w-6 h-6 text-green-500" />
            </div>
            <p className="text-4xl font-bold text-green-500">
              {Math.round(
                (('abcdefghijklmnopqrstuvwxyz'.indexOf(
                  profile?.last_trained_alphabet || 'a'
                ) +
                  1) /
                  26) *
                  100
              )}
            </p>
            <p className="text-sm text-gray-400 mt-2">% Complete</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="stat-card rounded-2xl p-6 mb-8 border">
          <h3 className="text-xl font-bold text-white mb-4">Alphabet Progress</h3>
          <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full transition-all duration-300"
              style={{
                width: `${Math.round(
                  (('abcdefghijklmnopqrstuvwxyz'.indexOf(
                    profile?.last_trained_alphabet || 'a'
                  ) +
                    1) /
                    26) *
                    100
                )}%`,
              }}
            ></div>
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-400">
            <span>A</span>
            <span>Current: {profile?.last_trained_alphabet?.toUpperCase() || 'A'}</span>
            <span>Z</span>
          </div>
        </div>

        {/* Action Button */}
        <div className="text-center">
          <Link to="/" className="btn-primary inline-block" data-testid="continue-practice-btn">
            Continue Practice
          </Link>
        </div>
      </div>
    </div>
  )
}
